import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";

export default function useChatBot(authAxios) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const sseRef = useRef(null);
  const sseRetryRef = useRef(null);
  const sseFailCountRef = useRef(0);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [chatBotEnabled, setChatBotEnabled] = useState(true);
  const [connected, setConnected] = useState(false);
  const [waStatus, setWaStatus] = useState("disconnected");
  const [waDisconnectReason, setWaDisconnectReason] = useState("");
  const [v2Running, setV2Running] = useState(false);
  const [connectedPhone, setConnectedPhone] = useState(null);
  const [botSettings, setBotSettings] = useState({});
  const [sending, setSending] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const messagesEndRef = useRef(null);
  const selectedContactId = selectedContact?.id || null;
  const selectedRestaurantId = selectedContact?.restaurant_id || "self";

  // Ref so fetchContacts can read selectedContact without being a dependency
  const selectedContactRef = useRef(null);
  selectedContactRef.current = selectedContact;

  // Ref so reactive refresh can call fetchMessages without circular deps
  const fetchMessagesRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchMessages = useCallback(async (cid) => {
    try {
      const r = await authAxiosRef.current.get(`/chatbot/messages/${cid}`);
      setMessages(r.data);
    } catch (err) {
      // Silent — don't toast on poll failure, thread just shows stale data
      console.warn("[CHATBOT] fetchMessages failed:", err?.response?.status, err?.message);
    }
  }, []);
  fetchMessagesRef.current = fetchMessages;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchContacts = useCallback(async () => {
    try {
      const r = await authAxiosRef.current.get("/chatbot/contacts");
      setContacts(prev => {
        // Reactive refresh: if selected contact has new activity, update thread immediately
        // without waiting for the next 1.5s message poll.
        const selId = selectedContactRef.current?.id;
        if (selId && fetchMessagesRef.current) {
          const prevContact = prev.find(c => c.id === selId);
          const nextContact = r.data.find(c => c.id === selId);
          if (nextContact && prevContact &&
              nextContact.last_message_at !== prevContact.last_message_at) {
            fetchMessagesRef.current(selId);
          }
        }
        return r.data;
      });
    } catch (err) {
      console.warn("[CHATBOT] fetchContacts failed:", err?.response?.status, err?.message);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchSettings = useCallback(async () => {
    try {
      const r = await authAxiosRef.current.get("/chatbot/settings");
      setBotSettings(r.data);
      setChatBotEnabled(r.data.enabled !== false);
      setConnected(r.data.connected || false);
      setConnectedPhone(r.data.phone || null);
    } catch {}
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchWAStatus = useCallback(async () => {
    try {
      const r = await authAxiosRef.current.get("/chatbot/whatsapp/status");
      const status = r.data?.status || "disconnected";
      setWaStatus(status);
      setWaDisconnectReason(r.data?.disconnect_reason || "");
      setV2Running(r.data?.v2_running === true);
      setConnected(status === "connected");
      if (r.data?.phone) setConnectedPhone(r.data.phone);
    } catch {
      // network error — keep last known state, don't flip to disconnected
    }
  }, []);

  const closeSSE = useCallback(() => {
    if (sseRetryRef.current) {
      clearTimeout(sseRetryRef.current);
      sseRetryRef.current = null;
    }
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
  }, []);

  const buildSSEUrl = useCallback((restaurantId, contactId) => {
    const baseURL = authAxiosRef.current?.defaults?.baseURL || "/api";
    const cleanBase = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
    const params = new URLSearchParams();
    if (contactId) params.set("contact_id", contactId);
    const query = params.toString();
    return `${cleanBase}/chatbot/stream/${encodeURIComponent(restaurantId)}${query ? `?${query}` : ""}`;
  }, []);

  // Load settings on mount; poll WA status every 15s
  useEffect(() => {
    fetchSettings();
    fetchWAStatus();
    const waStatusInterval = setInterval(fetchWAStatus, 15000);
    return () => {
      clearInterval(waStatusInterval);
    };
  }, [fetchSettings, fetchWAStatus]);

  // Real-time updates via SSE with polling fallback
  useEffect(() => {
    fetchContacts();
    if (selectedContactId) fetchMessages(selectedContactId);

    if (typeof window === "undefined" || !window.EventSource) {
      const contactsInterval = setInterval(fetchContacts, 3000);
      const messagesInterval = selectedContactId
        ? setInterval(() => fetchMessages(selectedContactId), 4000)
        : null;
      return () => {
        clearInterval(contactsInterval);
        if (messagesInterval) clearInterval(messagesInterval);
      };
    }

    closeSSE();

    const connectSSE = () => {
      const accessToken = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
      if (!accessToken) {
        return;
      }
      if (sseFailCountRef.current >= 8) {
        return;
      }
      const streamUrl = buildSSEUrl(selectedRestaurantId, selectedContactId);
      const source = new window.EventSource(streamUrl, { withCredentials: true });
      sseRef.current = source;

      source.onmessage = (event) => {
        sseFailCountRef.current = 0;
        try {
          const parsed = JSON.parse(event.data || "{}");
          if (parsed.type === "contacts" && Array.isArray(parsed.payload)) {
            setContacts(parsed.payload);
            return;
          }
          if (parsed.type === "messages") {
            const payload = parsed.payload || {};
            const streamContactId = payload.contact_id;
            if (selectedContactId && streamContactId === selectedContactId) {
              setMessages(Array.isArray(payload.messages) ? payload.messages : []);
            }
          }
        } catch {}
      };

      source.onerror = () => {
        closeSSE();
        sseFailCountRef.current += 1;
        const delay = Math.min(3000 * Math.pow(2, sseFailCountRef.current - 1), 60000);
        sseRetryRef.current = setTimeout(() => {
          connectSSE();
        }, delay);
      };
    };

    connectSSE();
    return () => closeSSE();
  }, [selectedContactId, selectedRestaurantId, fetchContacts, fetchMessages, buildSSEUrl, closeSSE]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Poll active AI session for selected contact
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!selectedContact?.phone) { setActiveSession(null); return; }
    const fetchSession = async () => {
      try {
        const r = await authAxiosRef.current.get(`/chatbot/sessions/${selectedContact.phone}`);
        setActiveSession(r.data?.id ? r.data : null);
      } catch { setActiveSession(null); }
    };
    fetchSession();
    const i = setInterval(fetchSession, 5000);
    return () => clearInterval(i);
  }, [selectedContact]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedContact) return;

    // Block send if WhatsApp is not connected
    if (!connected) {
      toast.error("WhatsApp desconectado — reconecte antes de enviar mensagens.");
      return;
    }

    // Command shortcuts to control Karol per conversation.
    // Normalize: remove dots/punctuation, collapse spaces, uppercase.
    // Accepts: ENTRAR / ENTRAR IA / ENTRAR I.A  and  SAIR / SAIR IA / SAIR I.A
    const cmd = newMessage.trim().toUpperCase().replace(/[^A-Z0-9\s]/g, "").replace(/\s+/g, " ").trim();
    if (cmd === "ENTRAR" || cmd === "ENTRAR IA") {
      setNewMessage("");
      if (selectedContact.bot_paused) await toggleContactBot(selectedContact);
      else toast.info("Karol já está ativa nesta conversa");
      return;
    }
    if (cmd === "SAIR" || cmd === "SAIR IA") {
      setNewMessage("");
      if (!selectedContact.bot_paused) await toggleContactBot(selectedContact);
      else toast.info("Karol já está pausada nesta conversa");
      return;
    }

    setSending(true);
    try {
      await authAxios.post("/chatbot/send", {
        contact_id: selectedContact.id,
        phone: selectedContact.phone,
        text: newMessage.trim(),
      });
      setNewMessage("");
      fetchMessages(selectedContact.id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : typeof detail === "object" ? (detail?.message || JSON.stringify(detail))
        : err?.message || "Erro ao enviar mensagem";
      toast.error(msg);
    }
    setSending(false);
  };

  const toggleBot = async () => {
    const n = !chatBotEnabled;
    setChatBotEnabled(n);
    try {
      await authAxios.patch("/chatbot/settings", { enabled: n });
      toast.success(n ? "ChatBot activado" : "ChatBot desactivado");
    } catch { setChatBotEnabled(!n); }
  };

  const toggleContactBot = async (contact) => {
    if (!contact) return;
    const newPaused = !contact.bot_paused;
    const now = new Date().toISOString();
    const optimistic = { bot_paused: newPaused, bot_paused_explicit: false, bot_paused_at: newPaused ? now : null };
    setSelectedContact(prev => prev?.id === contact.id ? { ...prev, ...optimistic } : prev);
    setContacts(prev => prev.map(c => c.id === contact.id ? { ...c, ...optimistic } : c));
    try {
      await authAxios.patch(`/chatbot/contacts/${contact.id}/bot-pause`);
      toast.success(newPaused ? "IA pausada para esta conversa" : "IA retomada para esta conversa");
    } catch {
      const rollback = { bot_paused: !newPaused, bot_paused_explicit: contact.bot_paused_explicit, bot_paused_at: contact.bot_paused_at };
      setSelectedContact(prev => prev?.id === contact.id ? { ...prev, ...rollback } : prev);
      setContacts(prev => prev.map(c => c.id === contact.id ? { ...c, ...rollback } : c));
      toast.error("Erro ao alterar estado da IA");
    }
  };

  const saveSettings = async (data) => {
    try {
      await authAxios.patch("/chatbot/settings", data);
      setBotSettings(p => ({ ...p, ...data }));
      toast.success("Guardado!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : err?.message || "Erro ao guardar";
      toast.error(msg);
    }
  };

  const handleDisconnect = async () => {
    try {
      await authAxios.post("/chatbot/whatsapp/disconnect");
      setConnected(false);
      setConnectedPhone(null);
      toast.info("WhatsApp desconectado. Voce pode reconectar a qualquer momento.");
    } catch { toast.error("Erro ao desconectar"); }
  };

  const filteredContacts = contacts.filter(c =>
    (c.name || c.phone || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return {
    contacts,
    filteredContacts,
    selectedContact,
    setSelectedContact,
    messages,
    newMessage,
    setNewMessage,
    searchQuery,
    setSearchQuery,
    chatBotEnabled,
    connected,
    setConnected,
    waStatus,
    waDisconnectReason,
    v2Running,
    connectedPhone,
    botSettings,
    sending,
    activeSession,
    messagesEndRef,
    handleSend,
    toggleBot,
    toggleContactBot,
    saveSettings,
    handleDisconnect,
    fetchSettings,
    fetchWAStatus,
  };
}
